import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, ArrowLeft, Check, Eye, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';

type RequiredView = {
    type: string;
    name: string;
};

const DYNAMIC_DATA_TYPE = 'Standards';
const DYNAMIC_DATA_NAME = 'Must Include Views';

// Values match Autodesk.Revit.DB.ViewType.ToString().
const REVIT_VIEW_TYPES = [
    'FloorPlan',
    'CeilingPlan',
    'EngineeringPlan',
    'AreaPlan',
    'Elevation',
    'Section',
    'ThreeD',
    'DrawingSheet',
    'DraftingView',
    'Legend',
    'Schedule',
];

const GET_PROJECT_MUST_INCLUDE_VIEWS = gql`
    query getProjectMustIncludeViews($id: String) {
        Project(id: $id) {
            dynamicData { id name type data setData }
            healthCheckIds
        }
        HealthCheckList(where: { name: { eq: "Must Include Views" } }) {
            nodes { id name }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA = gql`
    mutation setMustIncludeViewsDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

const ASSIGN_PROJECT_HEALTH_CHECK = gql`
    mutation assignMustIncludeViewsHealthCheck($projectId: String, $healthCheckIds: [String]) {
        Project(id: $projectId) { setHealthChecks(ids: $healthCheckIds) }
    }
`;

type MustIncludeViewsStandardProps = {
    projectId: string;
    canEdit?: boolean;
    style?: CSSProperties;
    onBack: () => void;
};

const MustIncludeViewsStandard = ({
    projectId,
    canEdit = false,
    style,
    onBack,
}: MustIncludeViewsStandardProps) => {
    const [views, setViews] = useState<RequiredView[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isAssigningHealthCheck, setIsAssigningHealthCheck] = useState(false);
    const [healthCheckError, setHealthCheckError] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();
    const { data, loading, refetch } = useQuery(GET_PROJECT_MUST_INCLUDE_VIEWS, {
        variables: { id: projectId },
        skip: !projectId,
        fetchPolicy: 'network-only',
    });
    const [setDynamicData] = useMutation(SET_PROJECT_DYNAMIC_DATA);
    const [assignProjectHealthCheck] = useMutation(ASSIGN_PROJECT_HEALTH_CHECK);

    const existingEntry = useMemo(() => {
        const entries: any[] = data?.Project?.dynamicData ?? [];
        return entries.find(
            entry => entry?.type === DYNAMIC_DATA_TYPE && entry?.name === DYNAMIC_DATA_NAME,
        ) as { id: string; data?: string; setData?: string } | undefined;
    }, [data]);

    const matchingHealthChecks = useMemo(
        () => ((data?.HealthCheckList?.nodes ?? []) as Array<{ id?: string; name?: string }>)
            .filter(healthCheck => healthCheck?.id && healthCheck?.name === DYNAMIC_DATA_NAME),
        [data],
    );
    const assignedHealthCheckIds = (data?.Project?.healthCheckIds ?? []) as Array<string | null>;
    const isHealthCheckAssigned = matchingHealthChecks.some(
        healthCheck => healthCheck.id && assignedHealthCheckIds.includes(healthCheck.id),
    );
    const healthCheckToAssign = matchingHealthChecks.find(healthCheck => !!healthCheck.id);

    useEffect(() => {
        if (loading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        download(existingEntry.data)
            .then(raw => {
                const parsed = JSON.parse(typeof raw === 'string' ? raw : JSON.stringify(raw));
                if (!Array.isArray(parsed)) throw new Error('Must Include Views data must be an array');
                setViews(parsed
                    .filter(item => item && typeof item.type === 'string' && typeof item.name === 'string')
                    .map(item => ({ type: item.type, name: item.name })));
                setIsDirty(false);
            })
            .catch(() => setSaveStatus('error'))
            .finally(() => setIsLoadingBlob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, loading]);

    const updateView = useCallback((index: number, field: keyof RequiredView, value: string) => {
        setViews(current => current.map((view, i) => i === index ? { ...view, [field]: value } : view));
        setIsDirty(true);
        setSaveStatus('idle');
    }, []);

    const addView = useCallback(() => {
        setViews(current => current.concat({ type: 'FloorPlan', name: '' }));
        setIsDirty(true);
        setSaveStatus('idle');
    }, []);

    const removeView = useCallback((index: number) => {
        setViews(current => current.filter((_, i) => i !== index));
        setIsDirty(true);
        setSaveStatus('idle');
    }, []);

    const hasInvalidRows = views.some(view => !view.type.trim() || !view.name.trim());

    const handleSave = useCallback(async () => {
        if (hasInvalidRows) return;
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (!existingEntry) {
                await setDynamicData({
                    variables: {
                        projectId,
                        data: {
                            name: DYNAMIC_DATA_NAME,
                            type: DYNAMIC_DATA_TYPE,
                            description: 'Revit views that must be included in the model',
                        },
                    },
                });
            }

            const refreshed = await refetch();
            const entry: any = (refreshed.data?.Project?.dynamicData ?? []).find(
                (item: any) => item?.type === DYNAMIC_DATA_TYPE && item?.name === DYNAMIC_DATA_NAME,
            );
            if (!entry?.setData) throw new Error('No upload URL returned');

            const payload = views.map(view => ({ type: view.type.trim(), name: view.name.trim() }));
            await httpClient.put(entry.setData, JSON.stringify(payload), {
                headers: {
                    'x-ms-blob-type': 'BlockBlob',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                },
            });
            setViews(payload);
            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    }, [existingEntry, hasInvalidRows, httpClient, projectId, refetch, setDynamicData, views]);

    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    const handleAssignHealthCheck = useCallback(async () => {
        if (!healthCheckToAssign?.id) return;
        setIsAssigningHealthCheck(true);
        setHealthCheckError('');
        try {
            await assignProjectHealthCheck({
                variables: {
                    projectId,
                    healthCheckIds: [healthCheckToAssign.id],
                },
            });
            await refetch();
        } catch {
            setHealthCheckError('Unable to add the health check to this project.');
        } finally {
            setIsAssigningHealthCheck(false);
        }
    }, [assignProjectHealthCheck, healthCheckToAssign?.id, projectId, refetch]);

    const isLoading = loading || isLoadingBlob;

    return (
        <div className="flex flex-col h-full" style={style} data-testid="MustIncludeViewsStandard">
            <div
                className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md"
                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleBack}
                >
                    <ArrowLeft size={15} /> Back
                </button>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Must Include Views
                </span>
                <div className="flex-1" />
                {saveStatus === 'saved' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                        <Check size={13} /> Saved
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                        <AlertCircle size={13} /> {isLoadingBlob ? 'Unable to load data' : 'Save failed'}
                    </span>
                )}
                {isDirty && saveStatus === 'idle' && (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Unsaved changes</span>
                )}
                <button className="btn-secondary flex items-center gap-1.5 text-sm" onClick={addView}>
                    <Plus size={14} /> Add view
                </button>
                <button
                    className="btn-primary flex items-center gap-2 text-sm"
                    disabled={!isDirty || isSaving || hasInvalidRows}
                    onClick={handleSave}
                    style={{ opacity: !isDirty || isSaving || hasInvalidRows ? 0.5 : 1 }}
                    title={hasInvalidRows ? 'Every view must have a type and name' : undefined}
                >
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                </button>
            </div>

            {!isLoading && (
                <div className="px-6 pt-5">
                    {isHealthCheckAssigned ? (
                        <div
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                            style={{
                                color: 'var(--success, #16a34a)',
                                backgroundColor: 'var(--success-soft, #dcfce7)',
                                border: '1px solid var(--success, #16a34a)',
                            }}
                        >
                            <Check size={15} />
                            The required “{DYNAMIC_DATA_NAME}” health check is assigned to this project.
                        </div>
                    ) : matchingHealthChecks.length > 0 ? (
                        <div
                            className="flex items-center justify-between gap-4 px-3 py-2.5 rounded-lg"
                            style={{
                                color: 'var(--warning-text, #92400e)',
                                backgroundColor: 'var(--warning-soft, #fef3c7)',
                                border: '1px solid var(--warning, #d97706)',
                            }}
                        >
                            <span className="flex items-center gap-2 text-sm">
                                <AlertCircle size={15} className="flex-shrink-0" />
                                This standard requires the “{DYNAMIC_DATA_NAME}” health check.
                            </span>
                            {canEdit ? (
                                <button
                                    className="btn-secondary flex items-center gap-1.5 text-sm flex-shrink-0"
                                    disabled={isAssigningHealthCheck}
                                    onClick={handleAssignHealthCheck}
                                >
                                    {isAssigningHealthCheck
                                        ? <Loader2 size={14} className="animate-spin" />
                                        : <Plus size={14} />}
                                    Add to Project
                                </button>
                            ) : (
                                <span className="text-xs flex-shrink-0">
                                    A Project Admin must add it.
                                </span>
                            )}
                        </div>
                    ) : (
                        <div
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                            style={{
                                color: 'var(--danger, #dc2626)',
                                backgroundColor: 'var(--danger-soft, #fee2e2)',
                                border: '1px solid var(--danger, #dc2626)',
                            }}
                        >
                            <AlertCircle size={15} />
                            No health check named “{DYNAMIC_DATA_NAME}” is available. A system admin must create it before it can be assigned.
                        </div>
                    )}
                    {healthCheckError && (
                        <p className="flex items-center gap-1 mt-2 text-xs" style={{ color: 'var(--danger)' }}>
                            <AlertCircle size={13} /> {healthCheckError}
                        </p>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center flex-1">
                    <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                </div>
            ) : views.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 gap-4 px-6">
                    <div className="rounded-xl p-10 text-center" style={{ border: '2px dashed var(--border-default)', maxWidth: 440 }}>
                        <Eye size={24} className="mx-auto mb-3" style={{ color: 'var(--text-tertiary)' }} />
                        <p className="text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>No required views</p>
                        <p className="text-xs mb-5" style={{ color: 'var(--text-tertiary)' }}>
                            Specify the Revit views that every model must contain.
                        </p>
                        <button className="btn-primary flex items-center gap-2 text-sm mx-auto" onClick={addView}>
                            <Plus size={14} /> Add view
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-auto px-6 py-5">
                    <div className="card overflow-hidden" style={{ border: '1px solid var(--border-default)', maxWidth: 900 }}>
                        <div
                            className="grid grid-cols-[minmax(180px,0.7fr)_minmax(240px,1.3fr)_44px] gap-3 px-4 py-2 text-xs font-semibold border-b"
                            style={{ color: 'var(--text-secondary)', background: 'var(--surface-1, var(--surface-100))', borderColor: 'var(--border-default)' }}
                        >
                            <span>Revit view type</span>
                            <span>View name</span>
                            <span />
                        </div>
                        {views.map((view, index) => (
                            <div
                                key={index}
                                className="grid grid-cols-[minmax(180px,0.7fr)_minmax(240px,1.3fr)_44px] gap-3 items-center px-4 py-2.5 border-b"
                                style={{ borderColor: 'var(--border-subtle, var(--border-default))' }}
                            >
                                <select
                                    className="input-base text-sm w-full"
                                    value={view.type}
                                    onChange={event => updateView(index, 'type', event.target.value)}
                                    aria-label={`View type for row ${index + 1}`}
                                >
                                    {!REVIT_VIEW_TYPES.includes(view.type) && <option value={view.type}>{view.type}</option>}
                                    {REVIT_VIEW_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                                </select>
                                <input
                                    className="input-base text-sm w-full"
                                    value={view.name}
                                    onChange={event => updateView(index, 'name', event.target.value)}
                                    placeholder="e.g. Site Plan"
                                    aria-label={`View name for row ${index + 1}`}
                                />
                                <button
                                    className="p-2 rounded-md"
                                    style={{ color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}
                                    onClick={() => removeView(index)}
                                    title="Remove view"
                                    aria-label={`Remove ${view.name || `view row ${index + 1}`}`}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        ))}
                    </div>
                    {hasInvalidRows && (
                        <p className="flex items-center gap-1 mt-3 text-xs" style={{ color: 'var(--danger)' }}>
                            <AlertCircle size={13} /> Every required view must have a type and name.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default MustIncludeViewsStandard;
